import { useEffect, useRef } from 'react';

import {
  useWebSocket,
  WebSocketMessage,
  WebSocketOptions,
} from '@/hooks/useWebSocket';

interface SubscriptionPattern {
  pattern: string;
  matchType: 'prefix' | 'exact';
}

interface UseRealtimeSubscriptionOptions extends WebSocketOptions {
  /** Key patterns to subscribe to */
  patterns: SubscriptionPattern[];
  /** Enable/disable the subscription */
  enabled?: boolean;
  /** Callback when a matching update is received */
  onUpdate?: (message: WebSocketMessage) => void;
}

interface UseRealtimeSubscriptionReturn {
  isSubscribed: boolean;
  isConnected: boolean;
}

export function useRealtimeSubscription(
  options: UseRealtimeSubscriptionOptions
): UseRealtimeSubscriptionReturn {
  const { patterns, user, enabled = true, onUpdate } = options;

  const { isConnected, send, subscribe } = useWebSocket({ user });
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  });

  // Stable serialization of patterns for dependency tracking
  const patternsKey = JSON.stringify(
    patterns.map(p => `${p.pattern}:${p.matchType}`).sort()
  );

  // Subscribe to the WebSocket message stream and filter relevant messages
  useEffect(() => {
    if (!enabled || !isConnected) return;

    const unsubscribe = subscribe((message: WebSocketMessage) => {
      if (message.type !== 'update') return;

      const matches = patterns.some(p => {
        if (p.matchType === 'prefix') {
          return message.key.startsWith(p.pattern);
        }
        return message.key === p.pattern;
      });

      if (matches && onUpdateRef.current) {
        onUpdateRef.current(message);
      }
    });

    return unsubscribe;
  }, [enabled, isConnected, subscribe, patternsKey]);

  // Send subscribe request and capture subscription IDs for cleanup.
  // A requestId nonce correlates the response to this specific hook instance
  // so multiple concurrent subscriptions don't cross-capture each other's IDs.
  const subIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (!enabled || !isConnected || patterns.length === 0) return;

    const requestId = crypto.randomUUID();

    const unsubscribeListener = subscribe(
      (
        message: WebSocketMessage & {
          action?: string;
          requestId?: string;
          subscriptionIds?: string[];
        }
      ) => {
        if (
          message.action === 'subscribed' &&
          message.requestId === requestId &&
          message.subscriptionIds
        ) {
          subIdsRef.current = message.subscriptionIds;
        }
      }
    );

    send({
      action: 'subscribe',
      requestId,
      subscriptions: patterns.map(p => ({
        pattern: p.pattern,
        matchType: p.matchType,
      })),
    });

    return () => {
      unsubscribeListener();
      if (subIdsRef.current.length > 0) {
        send({
          action: 'unsubscribe',
          subscriptionIds: subIdsRef.current,
        });
        subIdsRef.current = [];
      }
    };
  }, [enabled, isConnected, send, subscribe, patternsKey]);

  const isSubscribed = enabled && isConnected && patterns.length > 0;

  return { isSubscribed, isConnected };
}
